import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { Classification } from './classification';
import { environment } from '../../../environments/environment';
import { ClassificationItem } from '../../common/model/classification-item';

@Injectable({
  providedIn: 'root',
})
export class ClassificationService {
  constructor(private http: HttpClient) {}

  findAll(params: HttpParams): Observable<any> {
    const url = `${environment.url.api}/u/classifications`;
    return this.http
      .get<any>(url, { params })
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  findOne(id: string): Observable<any> {
    const url = `${environment.url.api}/a/classifications/${id}`;
    return this.http.get<Classification>(url).pipe(timeout(environment.config.apiWaitingTime));
  }

  findItems(id: string, params: HttpParams): Observable<any> {
    const url = `${environment.url.api}/a/classifications/${id}/items`;
    return this.http
      .get<any>(url, { params })
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  findItemIdentification(id: string, params: HttpParams): Observable<any> {
    const url = `${environment.url.api}/a/classifications/${id}/items/identification`;
    return this.http
      .get<any>(url, { params })
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  reviewItems(id: string, params: HttpParams): Observable<any> {
    const url = `${environment.url.api}/a/classifications/${id}/items/review`;
    return this.http
      .get<any>(url, { params })
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  fetchSuggestions(params: HttpParams) {
    const url = `${environment.url.api}/a/classifications/search`;
    return this.http
      .get<string[]>(url, { params })
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  checkAppliedItem(items: ClassificationItem[]): Observable<any> {
    const url = `${environment.url.api}/a/classifications/check-applied-items`;
    return this.http
      .post<Classification>(url, items)
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  create(params: HttpParams, form: Classification): Observable<any> {
    const url = `${environment.url.api}/a/classifications`;
    return this.http.post<Classification>(url, form, { params }).pipe(timeout(environment.config.apiWaitingTime));
  }

  updateItems(id: string, form: ClassificationItem[]) {
    const url = `${environment.url.api}/a/classifications/${id}`;
    return this.http
      .put<any>(url, form)
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  countWaitingItems() {
    const url = `${environment.url.api}/a/classifications/waiting`;
    return this.http
      .get<any>(url)
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  downloadClassificationCsv(id: string) {
    const url = `${environment.url.api}/files/classification/${id}`;
    return this.http.get(url, {
      responseType: 'blob',
      headers: new HttpHeaders({
        'Accept': 'application/octet-stream'
      })
    });
  }

  exportClassificationItemsCsv(id: string, params: HttpParams) {
    const url = `${environment.url.api}/a/classifications/${id}/download`;
    return this.http.get(url, {
      responseType: 'blob',
      params: params,
      headers: new HttpHeaders({
        'Accept': 'text/csv'
      })
    });
  }

  startClassification(id: string, params?: HttpParams) {
    const url = `${environment.url.api}/a/classifications/${id}/reclassify`;
    return this.http
      .put<any>(url, null, { params })
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  findAllSummaryById(id: string, params: HttpParams): Observable<any> {
    const url = `${environment.url.api}/u/classifications/${id}/summaries`;
    return this.http
      .get<any>(url, { params })
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  // uploadToMdb(form: Classification) {
  //   const url = `${environment.url.api}/a/classifications/upload`;
  //   return this.http
  //     .post<any>(url, form)
  //     .pipe(timeout(environment.config.apiWaitingTime));
  // }

  readSearchResult(id: string, params: HttpParams): Observable<any> {
    const url = `${environment.url.api}/classification/${id}/read-search-result`;
    return this.http
      .get(url, { params })
      .pipe(timeout(environment.config.apiWaitingTime));
  }

  requestExampleCsv(params: HttpParams) {
    const url = `${environment.url.api}/a/classifications/example/csv`;
    return this.http.get(url, {
      responseType: 'blob',
      headers: new HttpHeaders({
        'Accept': 'application/octet-stream'
      }),
      params: params
    });
  }
}
